import { defineStore } from 'pinia';
import { ref } from 'vue';
import apiClient from '@/services/api/client';
import type { User } from '@/modules/admin/interfaces/User';
import type { UserLogin, UserCreditials, ResetPasswordValues} from '@/modules/admin/interfaces';

import { Endpoints } from '@/config/Urls';
import { useAuthStore } from '@/modules/admin/stores/auth';
import { useInterfaceUnlock } from '@/composables/useInterfaceUnlock';

interface UserData {
  user: User;
  accessToken?: string;
}

export const useUserApiStore = defineStore('api/user', () => {
  const isLoading = ref(false);
  const error = ref<Error | null>(null);

  const authStore = useAuthStore();

  async function loginUser(credentials: UserLogin): Promise<UserData> {
    isLoading.value = true;
    error.value = null;
    try {
      const response = await apiClient.post<UserData>(Endpoints.LOGIN_USER, credentials);
      authStore.setUser(response.data.user);
      if (response.data.accessToken) {
        authStore.setToken(response.data.accessToken);
      }
      return response.data;
    } catch (err) {
      error.value = err as Error;
      throw err;
    } finally {
      isLoading.value = false;
    }
  }

  async function registerUser(credentials: UserCreditials): Promise<UserData> {
    isLoading.value = true;
    error.value = null;
    try {
      const response = await apiClient.post<UserData>(Endpoints.REGISTER_USER, credentials);
      authStore.setUser(response.data.user);
      return response.data;
    } catch (err) {
      error.value = err as Error;
      throw err;
    } finally {
      isLoading.value = false;
    }
  }

  async function getUser(): Promise<User> {
    isLoading.value = true;
    error.value = null;
    try {
      const response = await apiClient.get<User>(Endpoints.ME);
      authStore.setUser(response.data);
      return response.data;
    } catch (err) {
      error.value = err as Error;
      throw err;
    } finally {
      isLoading.value = false;
    }
  }

  async function forgotPassword(email: { email: string }): Promise<void> {
    isLoading.value = true;
    error.value = null;
    try {
      await apiClient.post(Endpoints.FORGOT_PASSWORD, email);
    } catch (err) {
      error.value = err as Error;
      throw err;
    } finally {
      isLoading.value = false;
    }
  }

  async function resetPassword(values: ResetPasswordValues): Promise<void> {
    isLoading.value = true;
    error.value = null;
    try {
      await apiClient.post(Endpoints.RESET_PASSWORD, values);
    } catch (err) {
      error.value = err as Error;
      throw err;
    } finally {
      isLoading.value = false;
    }
  }

  async function updateUser(id: number, body: Partial<User>): Promise<void> {
    isLoading.value = true;
    error.value = null;
    try {
      await apiClient.put(`${Endpoints.USERS}/${id}`, { data: body });
      // Optionally refresh user data
      await getUser();
    } catch (err) {
      error.value = err as Error;
      throw err;
    } finally {
      isLoading.value = false;
    }
  }

  async function changePassword(id: number, currentPassword: string, newPassword: string): Promise<void> {
    isLoading.value = true;
    error.value = null;
    try {
      await apiClient.put(`${Endpoints.USERS}/${id}`, { 
        data: { 
          password: newPassword,
          currentPassword: currentPassword 
        } 
      });
    } catch (err) {
      error.value = err as Error;
      throw err;
    } finally {
      isLoading.value = false;
    }
  }

  /**
   * Clear local auth state immediately so callers can navigate to /login without
   * the router guard bouncing them back (the guard reads auth state synchronously),
   * then best-effort tell the backend to record AUTH_LOGOUT (cookie still sent).
   */
  async function logoutUser(): Promise<void> {
    const wasLoggedIn = !!authStore.user;
    authStore.logoutUser();
    // Drop the cached Trivium Lite unlock state so a different account
    // logging in on this tab refetches its own state.
    useInterfaceUnlock().resetUnlockState();
    if (wasLoggedIn) {
      try {
        await apiClient.post(Endpoints.LOGOUT);
      } catch {
        // Local logout already applied; ignore backend logout failures.
      }
    }
  }

  return {
    isLoading,
    error,
    loginUser,
    registerUser,
    getUser,
    forgotPassword,
    resetPassword,
    updateUser,
    changePassword,
    logoutUser,
  };
});